import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useId, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { formatarDataHora } from '@/lib/formato';
import { comCorpo, explicarFalha, pedir } from '@/lib/pedido';
import { useSessao } from '@/modulos/sessao/consultas';
import { UtilidadeDaResposta } from '@/modulos/tecnico/UtilidadeDaResposta';

import { contagem } from './painel';

/**
 * IA 04 — o resumo operacional com evidências. Parte dos números deste painel,
 * com as mesmas permissões, e cita cada fato que usou: a lista embaixo do
 * resumo é o que ele leu, por extenso. Resumo que não cita fato nenhum não é
 * mostrado como resumo.
 */

interface Resumo {
  readonly id: string;
  readonly sabe: boolean;
  readonly resposta: string;
  readonly citacoes: readonly {
    readonly trechoId: string;
    readonly texto: string;
    /**
     * A tela onde quem lê pode agir sobre aquele fato (v6, ferramenta 4 do
     * módulo 13).
     *
     * Opcional, e por isso a lista não desenha link quando falta: citação
     * gravada é registro histórico, e as de antes de 02/10 não guardaram
     * destino. Adivinhar a tela pelo texto levaria quem clica a um lugar que
     * talvez não fale daquele fato.
     */
    readonly para?: string | null;
  }[];
  readonly falta: string | null;
  readonly criadoEm: string;
  readonly pedidoPor?: string | null;
  /**
   * Para quem o resumo foi escrito (ferramenta 4 do modulo 13).
   *
   * Vem da coluna que guarda a frase do pedido, e por isso resumo de ANTES de
   * 02/10 nao a tem — eles foram escritos sem angulo, e dizer que tinham um
   * seria inventar o passado. Opcional de proposito.
   */
  readonly escritoPara?: string | null;
  /** Se quem está vendo pediu este resumo: só ele o avalia (0076). */
  readonly minha?: boolean;
  readonly minhaUtilidade?: boolean | null;
}

/**
 * De quem é o ângulo, a partir da frase gravada no pedido.
 *
 * A frase é `Resumo operacional do painel para <quem>`. Ler o sufixo é o
 * preço de não ter criado coluna para isso — e a coluna custaria migração,
 * preenchimento das linhas antigas (que não tiveram ângulo) e uma segunda
 * fonte da mesma informação. Frase sem o " para " é resumo de antes, e aí não
 * se escreve nada.
 */
function paraQuem(pedido: string | null | undefined): string | null {
  const marca = ' para ';
  const i = (pedido ?? '').indexOf(marca);
  if (i === -1) return null;
  const quem = (pedido ?? '').slice(i + marca.length).trim();
  return quem === '' ? null : quem;
}

export function ResumoDaIa() {
  const sessao = useSessao();
  const pode = (sessao.data?.permissoes ?? []).includes('ia.perguntar');
  const cliente = useQueryClient();
  const [recusa, setRecusa] = useState<{ erro: string; comoResolver: string } | null>(null);
  const ultimos = useQuery({
    queryKey: ['ia', 'resumos'],
    queryFn: () => pedir<{ resumos: readonly Resumo[]; motorConfigurado: boolean }>('/painel/resumos'),
    enabled: pode,
  });
  const resumir = useMutation({
    mutationFn: () => comCorpo<Resumo>('POST', '/painel/resumo', {}),
    /* O resumo aparece no próprio cartão, e a recusa também (`setRecusa`). */
    meta: { silencioso: true, silenciarFalha: true },
    onSuccess: () => {
      setRecusa(null);
      void cliente.invalidateQueries({ queryKey: ['ia', 'resumos'] });
    },
    onError: (falha) => {
      setRecusa(explicarFalha(falha));
    },
  });
  const idDoMotivo = useId();
  if (!pode) return null;

  const atual = ultimos.data?.resumos[0] ?? null;
  /* Com o motor desligado, o único resultado possível do botão é a recusa:
     ele fica desabilitado, e o motivo escrito ao lado é a descrição dele. */
  const desligado = ultimos.data?.motorConfigurado === false;

  return (
    <section className="cartao flex flex-col gap-3 p-6">
      {/* Como o CabecalhoPagina (R01, p.28): quando o texto não cabe ao lado
          do botão, o botão desce para a linha de baixo, em vez de espremer a
          descrição numa coluna estreita ao lado dele. */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-[1_1_240px] flex-col gap-1">
          <h2 className="txt-secao text-tinta">Resumo da IA</h2>
          <p id={idDoMotivo} className="txt-apoio text-pretty text-tinta-secundaria">
            {desligado
              ? 'O assistente ainda não está ligado neste ambiente.'
              : 'Oito frases no máximo, a partir dos números deste painel. Cada frase cita o fato que a sustenta. Cada resumo tem custo.'}
          </p>
        </div>
        <Botao
          nivel="consulta"
          className="ml-auto"
          disabled={resumir.isPending || desligado}
          {...(desligado ? { 'aria-describedby': idDoMotivo } : {})}
          onClick={() => { resumir.mutate(); }}
        >
          {resumir.isPending ? 'Resumindo…' : 'Resumir painel'}
        </Botao>
      </div>

      {recusa ? (
        <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
          <span className="txt-corpo text-tinta">{`${recusa.erro.charAt(0).toUpperCase()}${recusa.erro.slice(1)}.`}</span>
          <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
        </div>
      ) : null}

      {/* Carregando (p.32) na forma do resumo: a linha da data e o parágrafo.
          Enquanto a IA escreve um novo, o mesmo esqueleto fica no lugar do
          anterior — o resumo velho na tela, com o botão dizendo "Resumindo…",
          se lia como a resposta nova. */}
      {ultimos.isPending || resumir.isPending ? (
        <Carregando
          oQue={resumir.isPending ? 'Escrevendo o resumo do painel' : 'Carregando o último resumo'}
          className="flex flex-col gap-3 py-1"
        >
          <Barra className="h-3 w-64 max-w-full" />
          <Barra className="h-3 w-full" />
          <Barra className="h-3 w-11/12" />
          <Barra className="h-3 w-3/5" />
        </Carregando>
      ) : null}
      {/* Com a recusa à vista, "Nenhum resumo pedido ainda" contradizia o
          pedido que acabou de falhar (robôs no QAS, 20/10/2026). */}
      {!ultimos.isPending && !resumir.isPending && atual === null && recusa === null ? (
        <p className="txt-apoio text-tinta-secundaria">Nenhum resumo pedido ainda.</p>
      ) : null}
      {atual && !resumir.isPending ? (
        <article className="flex flex-col gap-2">
          <span className="txt-apoio text-tinta-apagada">
            {`${formatarDataHora(new Date(atual.criadoEm))}${atual.pedidoPor ? ` · pedido por ${atual.pedidoPor}` : ''} — os números podem ter mudado desde então.`}
          </span>
          {/* PARA QUEM ELE FOI ESCRITO. Sem dizer isso, dois resumos do mesmo
              painel, pedidos por perfis diferentes, pareceriam a IA mudando de
              opinião — e quem lesse o do colega acharia que estava vendo o
              seu. O resumo de antes de 02/10 não tem a frase, e aí nada é
              escrito: inventar o ângulo que ele não teve seria pior. */}
          {paraQuem(atual.escritoPara) ? (
            <span className="txt-apoio text-tinta-secundaria">{`Escrito para ${paraQuem(atual.escritoPara) ?? ''}.`}</span>
          ) : null}
          <p className="txt-corpo text-tinta">{atual.sabe ? atual.resposta : `Sem resumo: ${atual.falta ?? 'a resposta não citou os fatos do painel'}.`}</p>
          {atual.citacoes.length > 0 ? (
            <details>
              <summary className="txt-apoio cursor-pointer text-tinta-secundaria">
                {atual.citacoes.length === 1
                  ? 'O fato citado'
                  : `Os ${contagem(atual.citacoes.length, 'fato citado', 'fatos citados')}`}
              </summary>
              <ol className="mt-2 flex list-decimal flex-col gap-1 pl-5">
                {atual.citacoes.map((c) => (
                  <li key={c.trechoId} className="txt-apoio text-tinta-secundaria">
                    {c.texto}
                    {/* O LINK INTERNO, depois do fato e não no lugar dele: o
                        fato é a prova, e precisa continuar legível inteiro
                        para quem não vai clicar.
                        O destino vem do servidor (`TELA_DO_ASSUNTO`), e não é
                        reconferido aqui: repetir o mapa na tela daria dois
                        lugares para ele divergir. Se uma rota for renomeada e
                        uma citação antiga apontar para o caminho velho, o
                        clique cai na tela de endereço inexistente, que existe
                        e diz o que fazer — melhor do que um link que a tela
                        esconde sem explicar. */}
                    {c.para ? (
                      <>
                        {' '}
                        <Link to={c.para} className="text-tinta-secundaria underline">
                          Abrir a tela
                        </Link>
                      </>
                    ) : null}
                  </li>
                ))}
              </ol>
            </details>
          ) : null}
          {atual.minha === true ? <UtilidadeDaResposta key={atual.id} perguntaId={atual.id} inicial={atual.minhaUtilidade ?? null} /> : null}
        </article>
      ) : null}
    </section>
  );
}
